import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { SAVED_CONTACTS_RECORD_ID_QUERY } from '@general/graphql/contact.graph';
import { SAVED_COMPANIES_QUERY } from '@general/graphql/company.graph';
import { SAVED_ARTICLES_QUERY } from '@general/graphql/article.graph';
import { SAVED_PUBLICATIONS_QUERY } from '@general/graphql/publication.graph';
import { SAVED_EVENTS_QUERY } from '@general/graphql/event.graph';
import { SAVED_SESSIONS_QUERY } from '@general/graphql/session.graph';
import useAuthorized from '@hooks/useAuthorized';

const useFavoritesAll = () => {
    const { isLoggedIn } = useAuthorized();
    const { loading: isLoadingContacts, data: contacts } = useQuery(SAVED_CONTACTS_RECORD_ID_QUERY, {
        skip: !isLoggedIn,
    });

    const { loading: isLoadingCompanies, data: companies } = useQuery(SAVED_COMPANIES_QUERY, { skip: !isLoggedIn });
    const { loading: isLoadingArticles, data: articles } = useQuery(SAVED_ARTICLES_QUERY, { skip: !isLoggedIn });
    const { loading: isLoadingPublications, data: publications } = useQuery(SAVED_PUBLICATIONS_QUERY, {
        skip: !isLoggedIn,
    });
    const { loading: isLoadingEvents, data: events } = useQuery(SAVED_EVENTS_QUERY, { skip: !isLoggedIn });
    const { loading: isLoadingSessions, data: sessions } = useQuery(SAVED_SESSIONS_QUERY, { skip: !isLoggedIn });

    const [isLoadingAll, setIsLoadngAll] = useState(true);

    const getFavoriteIds = (data, typePlural) => {
        if (!data) return [];
        // graph service returning null for some saved items, probably only a staging issue
        const filtered = data?.[typePlural].filter(e => !!e);
        return filtered.map(c => c.recordId);
    };

    useEffect(() => {
        if (
            isLoadingContacts ||
            isLoadingCompanies ||
            isLoadingArticles ||
            isLoadingPublications ||
            isLoadingEvents ||
            isLoadingSessions
        ) {
            return;
        }

        setIsLoadngAll(false);
    }, [
        isLoadingContacts,
        isLoadingCompanies,
        isLoadingEvents,
        isLoadingArticles,
        isLoadingPublications,
        isLoadingSessions,
    ]);

    return {
        isLoadingAll,
        // maps to content_type from Algolia
        allFavoriteIds: {
            member: getFavoriteIds(contacts, 'contacts'),
            company: getFavoriteIds(companies, 'companies'),
            article: getFavoriteIds(articles, 'articles'),
            publication: getFavoriteIds(publications, 'publications'),
            session: getFavoriteIds(sessions, 'sessions'),
            event: getFavoriteIds(events, 'viewerEvents'),
        },
    };
};

export default useFavoritesAll;
